/**
 * Resolves the `apps/ledger-web` base URL — used by {@link StepUpDialog}
 * (`components/auth/step_up_dialog.tsx`) to link a user to
 * `/settings/security` to set up two-factor authentication. Sibling to
 * `resolveApiUrl()` in `lib/trpc.ts`: same `VITE_*` + dev-fallback pattern,
 * kept in the auth lib instead since this is purely an auth-flow concern
 * (nothing else in wagepro-web links to ledger-web).
 */
export function resolveLedgerWebUrl(): string {
  if (import.meta.env.VITE_LEDGER_WEB_URL) {
    return import.meta.env.VITE_LEDGER_WEB_URL;
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:3010';
  }
  return 'https://app.ledgerpro.ai';
}

/**
 * Build the `apps/ledger-web` URL that opens Ledger's dashboard for the given
 * entity — the "Back to Ledger" deep link every post-consent screen in this
 * app uses (issue #23952 first-run and no-payroll-role screens).
 *
 * The real Ledger web route for an entity dashboard is `/clients/:entityId`
 * (see `apps/ledger-web/src/routes/entities/*` — the router hangs entity
 * screens off `client_id`, and the client resolver treats `entityId` as the
 * client id for a direct-owner entity). The previously-shipped
 * `/firms/<firm>/entities/<entity>/dashboard` was a plausible-sounding path
 * that does not exist — it returned a 404 for every real user, which the
 * adversarial review of f2a602384 caught as HIGH-severity.
 *
 * Kept as a single exported helper so both call sites (FirstRunSetupScreen
 * "Back to Ledger" and ConnectLandingGate's NoPayrollRoleScreen) route
 * through the same string builder — a future rename in ledger-web fails one
 * spot instead of drifting between them.
 */
export function ledgerEntityDashboardUrl(entityId: string): string {
  return `${resolveLedgerWebUrl()}/clients/${entityId}`;
}
